type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

// Store the version when the app first loads
let currentAppVersion: string | null = null;

// Get current app version - store it on first load
const getCurrentVersion = async () => {
  if (currentAppVersion) {
    return currentAppVersion;
  }
  
  try {
    const response = await fetch('/version.json');
    const versionInfo = await response.json();
    currentAppVersion = versionInfo.version;
    console.log(`SW: Current app version stored: ${currentAppVersion}`);
    return currentAppVersion;
  } catch (error) {
    console.log('SW: Could not get current version, using fallback');
    currentAppVersion = '0.4.2';
    return currentAppVersion;
  }
};

// Check if a new version is available and force reload if found
const checkForAppUpdate = async () => {
  try {
    const response = await fetch('/version.json?t=' + Date.now()); // Cache busting
    const versionInfo = await response.json();
    const currentVersion = await getCurrentVersion();
    const latestVersion = versionInfo.version;
    
    if (currentVersion !== latestVersion) {
      console.log(`App update available: ${currentVersion} -> ${latestVersion}`);
      console.log('SW: Automatically reloading to apply update...');
      
      // Force reload to get the new version
      window.location.reload();
      return true;
    }
    return false;
  } catch (error) {
    console.log('Could not check for app updates:', error);
    return false;
  }
};

// Handle service worker update
const handleServiceWorkerUpdate = (registration: ServiceWorkerRegistration) => {
  const newWorker = registration.installing || registration.waiting;
  
  if (newWorker) {
    console.log('New service worker available, updating...');
    
    // Send skip waiting message to activate immediately
    newWorker.postMessage({ type: 'SKIP_WAITING' });
    
    // Listen for controlling change and reload automatically
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('New service worker took control, reloading...');
      window.location.reload();
    });
  }
};

export function register(config?: Config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const swUrl = `${process.env.PUBLIC_URL}/sw.js`;

    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register(swUrl)
        .then(async (registration) => {
          console.log('SW: Service Worker registered successfully');
          
          // Initialize current version on first load
          await getCurrentVersion();
          
          // Check for app updates periodically
          setInterval(checkForAppUpdate, 5 * 60 * 1000); // Check every 5 minutes
          
          // Handle immediate updates
          if (registration.waiting) {
            handleServiceWorkerUpdate(registration);
          }

          // Handle new installations
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (!newWorker) return;

            console.log('SW: New service worker installing...');

            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  // New update available
                  console.log('SW: New content available, will update on next visit');
                  handleServiceWorkerUpdate(registration);
                } else {
                  // First time installation
                  console.log('SW: Content cached for offline use');
                  if (config?.onSuccess) config.onSuccess(registration);
                }
              }
            });
          });

          // Listen for controller changes (when new SW takes control)
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            console.log('SW: New service worker controlling, reloading...');
            window.location.reload();
          });

          if (config?.onSuccess) config.onSuccess(registration);
        })
        .catch((err) => {
          console.error('SW: Service Worker registration failed:', err);
        });
    });

    // Check for updates when the app gains focus
    window.addEventListener('focus', () => {
      checkForAppUpdate();
    });
  }
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        console.log('SW: Unregistering service worker');
        return registration.unregister();
      })
      .catch((e) => console.error('SW: Error unregistering service worker:', e.message));
  }
}